/**
 * Which PDP layout a product takes: the compact HybridPDP (with its category
 * grids) or the normal PDP.
 *
 * ONE predicate for both sides (COM-830). The client uses it to pick the
 * layout (`product-details.tsx`); the server loader uses it to decide whether
 * to build the hybrid category grids at all (1–3 grids, each a Constructor
 * browse + SCAPI tile enrichment + an uncached `getCategory`). The two must not
 * drift: a server that says "not hybrid" while the client renders HybridPDP
 * paints a hybrid page with no grids, and the reverse burns the calls for a
 * layout nobody sees.
 *
 * Rules (unchanged from the inline client logic it replaces):
 *   - A telehealth product NEVER takes the hybrid path (ET-216). HybridPDP
 *     renders its own ProductBuyForm, so `?hybrid=true` (a typeable debug
 *     switch) or a stock-out flipping `orderable` to false would otherwise hand
 *     an Rx product a working Add to Cart.
 *   - Otherwise hybrid when `?hybrid=true` is in the URL OR the product's
 *     `inventory.orderable` is exactly `false` (missing inventory is NOT
 *     non-orderable).
 *
 * Pure and dependency-light on purpose: it is imported by the client route
 * module and the server loader alike.
 */
import { isTelehealthProduct } from "~/hec/types/product";

/** The URL switch that forces the hybrid layout. */
export const HYBRID_PARAM = "hybrid";

/** Minimal product shape the predicate reads. */
export type HybridPdpProduct = {
  c_ownerClass?: string | null;
  inventory?: { orderable?: boolean | null } | null;
};

/** True when the URL carries `?hybrid=true` (exact, case-sensitive). */
export function hasHybridParam(searchParams: URLSearchParams): boolean {
  return searchParams.get(HYBRID_PARAM) === "true";
}

/** True when this product renders as HybridPDP for this URL. */
export function isHybridPdp(
  product: HybridPdpProduct,
  searchParams: URLSearchParams,
): boolean {
  if (isTelehealthProduct(product)) return false;
  return hasHybridParam(searchParams) || product.inventory?.orderable === false;
}
